import { Table, Thead, Tbody, Tr, TableContainer } from "@chakra-ui/react";
import moment from "moment";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import { shortLoanAmount } from "utils/helpers";
import NoResults from "../NoResults";
import StatusBadge from "../StatusBadge";
import TableCell from "../TableCell";
import TableHeaderCell from "../TableHeaderCell";
import { getCustomCurrency } from "utils/uiconfighelper";

const PropertyLoansTable = ({
  small,
  status,
  property_id,
  borrower_id,
  borrower_company,
  columns = [
    "Date",
    "Team",
    "Loan Number",
    "property",
    "Borrower",
    "Name",
    "Type",
    "Status",
    "Target",
    "Amount",
    "Loan Officer",
    "Lien Type",
  ],
}: {
  small?: boolean;
  property_id?: string;
  borrower_id?: string;
  borrower_company?: string;
  status?: any[];
  columns?: string[];
}) => {
  const { user } = useTenant();

  const navigate = useNavigate();
  const viewLoan = (id: string) => {
    navigate(`/loans/${id}`);
  };

  const [loans, setLoans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTable, setRefreshTable] = useState(false);

  async function onLoad() {
    try {
      setLoading(true);

      const response = await apiRequest("/supabase/loans/selectByProperty", {
        filters: {
          status,
        },
        property_id,
        borrower_id,
        borrower_company,
      });

      setLoans(
        (response.loans || []).map((loan: any) => {
          let target = 0;
          if (loan.status === "underwriting") {
            target = 20;
          }
          if (loan.status === "awaiting_approval") {
            target = 40;
          }
          if (loan.status === "escrow") {
            target = 60;
          }
          if (loan.status === "ready_to_fund") {
            target = 80;
          }
          if (loan.status === "funded") {
            target = 100;
          }
          return {
            ...loan,
            target,
          };
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setRefreshTable(false); // Reset the refresh flag after triggering refresh
  }, [refreshTable]);

  useEffect(() => {
    // Fetch data whenever property_id or refreshTable changes
    onLoad();
  }, [property_id, refreshTable]);

  return loans.length > 0 ? (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            {columns.includes("Date") && <TableHeaderCell small={small}>Date</TableHeaderCell>}
            {columns.includes("Team") && <TableHeaderCell small={small}>Team</TableHeaderCell>}

            {columns.includes("Loan Number") && (
              <TableHeaderCell small={small}>Loan Number</TableHeaderCell>
            )}

            {user.type !== "borrower" && columns.includes("Borrower") && (
              <TableHeaderCell small={small}>Borrower</TableHeaderCell>
            )}

            {columns.includes("Name") && <TableHeaderCell small={small}>Name</TableHeaderCell>}

            {columns.includes("Lien Type") && (
              <TableHeaderCell small={small}>Lien Type</TableHeaderCell>
            )}

            {columns.includes("Status") && <TableHeaderCell small={small}>Status</TableHeaderCell>}

            {columns.includes("Type") && <TableHeaderCell small={small}>Type</TableHeaderCell>}

            {columns.includes("Amount") && (
              <TableHeaderCell isNumeric small={small}>
                Amount
              </TableHeaderCell>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {loans.map(r => (
            <Tr key={r.id} onClick={() => viewLoan(r.id)} cursor="pointer">
              {columns.includes("Date") && (
                <TableCell>
                  {r.funding_date
                    ? moment(r.funding_date, "YYYY-MM-DD").format("MMM DD[,] YYYY")
                    : moment(r.created_at, "YYYY-MM-DD").format("MMM DD[,] YYYY")}
                </TableCell>
              )}

              {columns.includes("Loan Number") && <TableCell>{`#${r.id.split("-")[0]}`}</TableCell>}

              {user.type !== "borrower" && columns.includes("Borrower") && (
                <TableCell>{`${r.borrower.first_name} ${r.borrower.last_name}`}</TableCell>
              )}

              {columns.includes("Name") && <TableCell>{r.name}</TableCell>}

              {columns.includes("Lien Type") && <TableCell>{r.property_lien_type}</TableCell>}

              {columns.includes("Status") && (
                <TableCell>
                  <StatusBadge loan={r} />
                  {/* {r.status_label} */}
                </TableCell>
              )}

              {columns.includes("Type") && <TableCell>{r.loan_type.title}</TableCell>}

              {columns.includes("Amount") && (
                <TableCell isNumeric>
                  {shortLoanAmount(r.amount, getCustomCurrency(user))}
                </TableCell>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  ) : (
    <NoResults loading={loading} message="No loans to display..." />
  );
};

export default PropertyLoansTable;
